/**
 * Hook for managing commission sources (known sender emails).
 * Used to configure which email addresses are scanned for commission files.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface CommissionSource {
  sourceId: string;
  senderEmail: string;
  senderDomain: string;
  displayName?: string;
  templateId?: string | null;
  status: 'active' | 'pending_review' | 'ignored';
  addedBy: string;
  addedAt: string;
  matchedFiles: number;
  lastMatchAt?: string;
}

interface GetCommissionSourcesResponse {
  builtInSenders: string[];
  companySources: CommissionSource[];
}

interface ManageCommissionSourceResponse {
  success: boolean;
  message: string;
}

interface UseCommissionSourcesReturn {
  builtInSenders: string[];
  companySources: CommissionSource[];
  loading: boolean;
  error: string | null;
  addSource: (senderEmail: string, displayName?: string) => Promise<void>;
  removeSource: (sourceId: string) => Promise<void>;
  isSubmitting: boolean;
  refetch: () => Promise<void>;
}

/**
 * Hook to manage commission sources for a company.
 * Provides functions to list, add, and remove sender email addresses.
 */
export function useCommissionSources(companyId: string | undefined): UseCommissionSourcesReturn {
  const [builtInSenders, setBuiltInSenders] = useState<string[]>([]);
  const [companySources, setCompanySources] = useState<CommissionSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch commission sources
  const fetchSources = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const getCommissionSources = httpsCallable<{ companyId: string }, GetCommissionSourcesResponse>(
        functions,
        'getCommissionSources'
      );

      const result = await getCommissionSources({ companyId });
      setBuiltInSenders(result.data.builtInSenders);
      setCompanySources(result.data.companySources);
    } catch (err: any) {
      console.error('Failed to fetch commission sources:', err);
      setError(err.message || 'Failed to load commission sources');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Initial fetch
  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // Add a new commission source
  const addSource = useCallback(async (senderEmail: string, displayName?: string): Promise<void> => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    setIsSubmitting(true);
    try {
      const manageCommissionSource = httpsCallable<
        { companyId: string; action: 'add'; senderEmail: string; displayName?: string },
        ManageCommissionSourceResponse
      >(functions, 'manageCommissionSource');

      await manageCommissionSource({ companyId, action: 'add', senderEmail, displayName });

      // Refetch sources after adding
      await fetchSources();
    } finally {
      setIsSubmitting(false);
    }
  }, [companyId, fetchSources]);

  // Remove a commission source
  const removeSource = useCallback(async (sourceId: string): Promise<void> => {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    setIsSubmitting(true);
    try {
      const manageCommissionSource = httpsCallable<
        { companyId: string; action: 'remove'; sourceId: string },
        ManageCommissionSourceResponse
      >(functions, 'manageCommissionSource');

      await manageCommissionSource({ companyId, action: 'remove', sourceId });

      // Refetch sources after removing
      await fetchSources();
    } finally {
      setIsSubmitting(false);
    }
  }, [companyId, fetchSources]);

  return {
    builtInSenders,
    companySources,
    loading,
    error,
    addSource,
    removeSource,
    isSubmitting,
    refetch: fetchSources,
  };
}
